import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import logo from '../assets/images/login-logo.png';
import loginImage from '../assets/images/loginpage.png';
import { broadcastAdminUpdate } from '../utils/api';
import { clearActiveCompany, getActiveCompany } from '../utils/companyContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3040';

const Login = () => {
    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (localStorage.getItem('authToken')) {
            const activeCompany = getActiveCompany();
            navigate(activeCompany ? '/dashboard' : '/select-company', { replace: true });
        }
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmedIdentifier = identifier.trim();
        const trimmedPassword = password.trim();

        if (!trimmedIdentifier || !trimmedPassword) {
            setError('Please enter both email/username and password.');
            return;
        }

        setError('');
        setIsSubmitting(true);

        try {
            const response = await fetch(`${API_BASE_URL}/api/admin/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: trimmedIdentifier,
                    password: trimmedPassword,
                }),
            });

            const result = await response.json().catch(() => null);

            if (!response.ok || result?.status !== 'SUCCESS') {
                throw new Error(result?.message || 'Invalid login credentials.');
            }

            const { token, admin } = result?.data ?? {};

            if (!token) {
                throw new Error('Login token not received from the server.');
            }

            localStorage.setItem('authToken', token);
            clearActiveCompany();
            if (admin) {
                const adminWithMenuCache = { ...admin, menusCachedAt: Date.now() };
                localStorage.setItem('authAdmin', JSON.stringify(adminWithMenuCache));
                broadcastAdminUpdate(adminWithMenuCache);
            } else {
                localStorage.removeItem('authAdmin');
                broadcastAdminUpdate(null);
            }

            navigate('/select-company', { replace: true });
        } catch (err) {
            setError(err.message || 'Unable to login. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="container-fluid p-0">
            <div className="row g-0">
                {/* Left side - Image */}
                <div className="col-md-7 d-none d-md-block" style={{ height: '100vh' }}>
                    <img
                        src={loginImage}
                        alt="Login"
                        className="w-100 h-100 object-fit-cover"
                        style={{ objectPosition: 'center' }}
                    />
                </div>

                {/* Right side - Login Form */}
                <div className="col-md-5 d-flex align-items-center">
                    <div className="w-100 px-4 px-md-5">
                        <div className="text-center mb-4">
                            <img
                                src={logo}
                                alt="Grace Super Market"
                                className="mb-1"
                                style={{ maxWidth: '120px' }}
                            />
                            <img
                                src={require('../assets/images/GRACESUPERMARKET.png')}
                                alt="GRACE SUPER MARKET"
                                className="mb-1"
                                style={{ maxWidth: '265px', maxHeight:'50px', display: 'block', margin: '0 auto' }}
                            />
                            <p className=" fw-bold mb-1 small">Human Resource Management System</p>
                            <h4 className="mb-3 fw-bold">Welcome 👋</h4>
                            <p className="text-muted small mb-4 text-start">Please login here</p>
                        </div>

                        <form onSubmit={handleSubmit} className="mt-2">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}
                            <div className="mb-4">
                                <input
                                    type="text"
                                    className="form-control bg-light border-0 focus-red"
                                    placeholder="Enter Username or E-Mail Address"
                                    value={identifier}
                                    onChange={(e) => {
                                        setIdentifier(e.target.value);
                                        if (error) {
                                            setError('');
                                        }
                                    }}
                                    autoComplete="username"
                                    style={{backgroundColor:'#f1f1f1 !important'}}
                                />
                            </div>
                            <div className="mb-4">
                                <div className="position-relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className="form-control bg-light border-0 focus-red"
                                        placeholder="Password"
                                        value={password}
                                        onChange={(e) => {
                                            setPassword(e.target.value);
                                            if (error) {
                                                setError('');
                                            }
                                        }}
                                        autoComplete="current-password"
                                        style={{ backgroundColor: '#f1f1f1 !important' }}
                                    />
                                    <button
                                        type="button"
                                        className="btn position-absolute end-0 top-50 translate-middle-y border-0 text-muted pe-3"
                                        style={{ background: 'none' }}
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showPassword ? <FaEye aria-hidden="true" /> : <FaEyeSlash aria-hidden="true" />}
                                    </button>
                                </div>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <div className="form-check">
                                    <input type="checkbox" className="form-check-input" id="remember" defaultChecked />
                                    <label className="form-check-label text-muted" htmlFor="remember">Remember Me</label>
                                </div>
                                <Link to="/forgot-password" className="text-danger text-decoration-none">
                                    Forgot password?
                                </Link>
                            </div>
                            <button type="submit" className="btn btn-danger w-100 rounded-1" disabled={isSubmitting}>
                                {isSubmitting ? 'Logging in...' : 'Login'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
